import { type HTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  status?: 'online' | 'offline' | 'away' | 'busy';
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getColorFromName(name: string): string {
  const colors = [
    'bg-primary-400',
    'bg-secondary-400',
    'bg-success-500',
    'bg-warning-500',
    'bg-danger-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, src, alt, name, size = 'md', status, ...props }, ref) => {
    const sizes = {
      xs: 'w-6 h-6 text-xs',
      sm: 'w-8 h-8 text-sm',
      md: 'w-10 h-10 text-sm',
      lg: 'w-12 h-12 text-base',
      xl: 'w-16 h-16 text-lg',
    };

    const statusSizes = {
      xs: 'w-1.5 h-1.5',
      sm: 'w-2 h-2',
      md: 'w-2.5 h-2.5',
      lg: 'w-3 h-3',
      xl: 'w-4 h-4',
    };

    const statusColors = {
      online: 'bg-success-500',
      offline: 'bg-slate-400',
      away: 'bg-warning-500',
      busy: 'bg-danger-500',
    };

    return (
      <div ref={ref} className={clsx('relative inline-flex', className)} {...props}>
        {src ? (
          <img
            src={src}
            alt={alt || name || 'Avatar'}
            className={clsx('rounded-full object-cover', sizes[size])}
          />
        ) : (
          <div
            className={clsx(
              'rounded-full flex items-center justify-center font-semibold text-white',
              sizes[size],
              name ? getColorFromName(name) : 'bg-slate-400'
            )}
          >
            {name ? getInitials(name) : '?'}
          </div>
        )}
        {status && (
          <span
            className={clsx(
              'absolute bottom-0 right-0 rounded-full border-2 border-white dark:border-slate-950',
              statusSizes[size],
              statusColors[status]
            )}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

export default Avatar;

// Avatar Group
export interface AvatarGroupProps extends HTMLAttributes<HTMLDivElement> {
  max?: number;
  children: React.ReactNode;
}

export function AvatarGroup({ className, max = 4, children, ...props }: AvatarGroupProps) {
  const childrenArray = Array.isArray(children) ? children : [children];
  const visible = childrenArray.slice(0, max);
  const remaining = childrenArray.length - max;

  return (
    <div className={clsx('flex -space-x-2', className)} {...props}>
      {visible}
      {remaining > 0 && (
        <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 border-2 border-white dark:border-slate-950 flex items-center justify-center text-sm font-medium text-slate-700 dark:text-slate-200">
          +{remaining}
        </div>
      )}
    </div>
  );
}

